import React from 'react';
import { faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/knowledge-vs-context-graphs.jpg
import mapsVsGpsImg from '../../../assets/images/blogs/Context-Graphs-Map-vs-GPS.png';

const KnowledgeVsContextGraphs = () => (
    <BlogPost
        title="Knowledge Graphs vs Context Graphs: Maps vs GPS"
        subtitle="A knowledge graph is a map. A context graph is your GPS navigation."
        category="Architecture"
        categoryIcon={faMapLocationDot}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={mapsVsGpsImg}
        imageAlt="Knowledge graphs versus context graphs - maps versus GPS navigation"
        ctaHeading="Ready for GPS-style decision navigation?"
        ctaText="Book a discovery call and we'll show you how context graphs guide agents through dynamic decision spaces."
        seoDescription="The fundamental difference between knowledge graphs (static maps of what exists) and context graphs (dynamic GPS navigation of decisions)."
        seoKeywords="knowledge graphs, context graphs, decision graphs, agentic AI, runtime execution, Nebulyx"
        slug="knowledge-vs-context-graphs"
    >
        <p>
            The terms "context" and "graph" are causing confusion between the AI and
            Knowledge Graph crowds.
        </p>

        <p>
            So I've put together my thoughts on the difference. We are in early days
            but maybe we need another term other than context.
        </p>

        <h3>Maps vs GPS</h3>

        <p>
            <strong>A knowledge graph is a map.</strong>
        </p>

        <p>
            A carefully crafted, predefined reference of what exists and how things relate.
            Cartography.
        </p>

        <p>
            You consult and query it. It answers: "Here's what we know."
        </p>

        <p>
            <strong>A context graph is your GPS navigation.</strong>
        </p>

        <p>
            It's not showing you the territory. It's pointing the way based on where you
            want to go (your current destination), recording where you are, where you've
            been, what turns you took, and what options you have right now.
        </p>

        <blockquote className="blog-post-quote">
            "It's not a reference - it's a runtime trace. The map doesn't change when
            you drive. The GPS navigation does!"
        </blockquote>

        <p>
            Knowledge graphs are built. Context graphs are generated - every time an
            agent runs.
        </p>

        <h3>The Lifecycle</h3>

        <p>
            Context graphs are described as "a living record of decision traces stitched
            across entities and time." Systems of record for decisions, not just objects.
        </p>

        <p>
            That's the accumulated view, but those traces have to come from somewhere.
        </p>

        <p>
            <strong>Live:</strong> The agent navigates the context graph in real time
            (your current GPS route).
        </p>

        <p>
            <strong>Persisted:</strong> Execution completes, the context graph becomes
            a decision trace (your trip history).
        </p>

        <p>
            <strong>Accumulated:</strong> Decision traces become organisational memory
            (all trips, searchable precedent).
        </p>

        <p>
            Same structure, different lifecycle stage. The session-scoped execution graph
            produces the traces that become organisational memory.
        </p>

        <h3>Where It Gets Interesting</h3>

        <p>
            Context graphs are rooted in process execution, not just data modelling.
        </p>

        <p>
            They're closer to a query plan, a state machine, or a runtime call graph
            than they are to an ontology.
        </p>

        <p>
            <strong>Dynamic state machines, not predefined procedures and workflows.</strong>
        </p>

        <p>
            This is where AI is forcing convergence between the data and software worlds.
        </p>

        <h3>Two Different Questions</h3>

        <p>
            <strong>Knowledge graphs ask:</strong> "What exists and how does it relate?"
        </p>

        <p>
            <strong>Process execution asks:</strong> "What's happening, what can happen
            next, and what triggered this?"
        </p>

        <p>
            Context graphs need both.
        </p>

        <p>
            Agents don't just query knowledge. They execute processes, discovering and
            navigating state transitions dynamically.
        </p>

        <p>
            The context graph is the structure they navigate. It's data and execution
            fused together.
        </p>

        <h3>The Convergence</h3>

        <p>
            That's why this conversation is so valuable. The future belongs to teams
            that can blend knowledge representation with processing models.
        </p>

        <p>
            Neither discipline alone gets you there.
        </p>

        <p>
            <strong>A knowledge graph tells you what exists.</strong>
        </p>

        <p>
            <strong>A context graph tells you what happened, what's happening, and why</strong>
            - assembled for this specific journey.
        </p>

        <p>
            One is cartography. The other is navigation. 🎯
        </p>
    </BlogPost>
);

export default KnowledgeVsContextGraphs;
